/**
 * Typed HTTP errors for server functions.
 *
 * TanStack Start server functions use an RPC transport: by default the
 * handler's thrown `Error` is serialized into a 200-OK response body, and the
 * client proxy re-throws on receipt. That works, but it makes DevTools show
 * every application-level failure as "200 OK", which is confusing when
 * inspecting the Network tab.
 *
 * `HttpError` is caught by the global `httpErrorMiddleware` (see
 * `./middleware.ts`), which calls `setResponseStatus(err.status)` before
 * re-throwing. The client still sees the same rich error object, but the
 * response now carries an accurate status code.
 */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

/** The status an error should answer with: its own for an HttpError, 500 for anything else. */
export function httpStatusOf(err: unknown): number {
  return err instanceof HttpError ? err.status : 500
}

export function notFound(what: string): never {
  throw new HttpError(404, `${what} not found`)
}

export function unauthorized(message = 'Unauthorized'): never {
  throw new HttpError(401, message)
}

export function forbidden(message = 'Forbidden'): never {
  throw new HttpError(403, message)
}

export function badRequest(message: string): never {
  throw new HttpError(400, message)
}

export function conflict(message: string): never {
  throw new HttpError(409, message)
}

/**
 * Sentinel prefix tagged onto the `syncCurrentUser` 409 message when an
 * already-existing row with the same email is found under a different
 * Keycloak `sub`. The web app matches on this prefix to render the
 * "did you log in with the correct user?" dialog with a sign-out action,
 * instead of dropping the user on a generic error line.
 *
 * Kept in shared (not duplicated as a magic string at both ends) so a
 * rename can't silently drift the server's emitted tag away from the
 * client's matcher.
 */
export const EMAIL_IDENTITY_CONFLICT_PREFIX = '[EMAIL_IDENTITY_CONFLICT]'

/**
 * Sentinel prefix tagged onto RPC error messages that originated from a 401
 * `HttpError`. Needed because TanStack Start serializes server-function
 * errors with seroval's shallow Error plugin, which drops the `HttpError`
 * subclass and its `status` field — only the message survives the wire.
 *
 * Applied at the RPC middleware boundary (not inside `unauthorized()`) so
 * the REST API surface keeps its plain error messages. The web SPA matches
 * via `isUnauthorizedError()` to trigger silent re-auth instead of letting
 * the RPC bubble as an opaque failure.
 */
export const UNAUTHORIZED_PREFIX = '[UNAUTHORIZED]'

/** True when `error.message` carries the `UNAUTHORIZED_PREFIX` sentinel. */
export function isUnauthorizedError(error: Error | null | undefined): boolean {
  return error?.message?.startsWith(UNAUTHORIZED_PREFIX) ?? false
}

export function badGateway(message: string): never {
  throw new HttpError(502, message)
}
